import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LayoutHeader from '../../layout/LayoutHeader';
import { promptsApi } from '../../utils/promptsApi';
import Modal from '../../components/Modal';
import { useModal } from '../../hooks/useModal';



function PromptsHub() {
  const navigate = useNavigate();
  const { modalConfig, showModal, showConfirm, closeModal } = useModal();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        setLoading(true);
        const userPrompts = await promptsApi.getUserPrompts();
        setPrompts(userPrompts);
      } catch (error) {
        console.error('Error fetching prompts:', error);
        setPrompts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPrompts();
  }, []);

  const handleCreatePrompt = () => {
    navigate('/prompts/create');
  };

  const handlePromptClick = (prompt) => {
    navigate(`/prompts/edit/${prompt._id}`);
  };

  const handleDeletePrompt = async (e, promptId) => {
    e.stopPropagation(); // Prevent clicking the prompt itself
    
    showConfirm(
      'Delete Prompt',
      'Are you sure you want to delete this prompt? This action cannot be undone.',
      async () => {
        try {
          await promptsApi.deletePrompt(promptId);
          setPrompts(prev => prev.filter(p => p._id !== promptId));
        } catch (error) {
          console.error('Error deleting prompt:', error);
          showModal('Delete Error', 'Failed to delete prompt. Please try again.', 'error');
        }
      },
      'warning',
      'Delete',
      'Cancel'
    );
  };

  if (loading) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center">
            <div className="text-white text-center py-[50px]">Loading prompts...</div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Custom Prompts</span>
            <div 
              className="text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors"
              onClick={handleCreatePrompt}
            >
              Create
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto pb-[100px]">
            {prompts.length === 0 ? (
              <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-semibold mb-2">No Custom Prompts Yet</h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                  Create custom prompts to streamline your AI conversations. 
                  Build prompts tailored to your music career needs.
                </p>
                <button 
                  onClick={handleCreatePrompt}
                  className="bg-[#FF2300] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#FF2300] transition-colors"
                >
                  Create Your First Prompt
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {prompts.map((prompt) => (
                  <div 
                    key={prompt._id} 
                    onClick={() => handlePromptClick(prompt)}
                    className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 cursor-pointer hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)] transition-all relative group"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-white text-[12px] font-normal">{prompt.title}</h3>
                      <button
                        onClick={(e) => handleDeletePrompt(e, prompt._id)}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-300 transition-all text-[14px] ml-2"
                      >
                        ×
                      </button>
                    </div>
                    {prompt.description && (
                      <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-3 leading-relaxed">
                        {prompt.description}
                      </p>
                    )}
                    <div className="bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded p-3 text-[12px] text-[rgba(255,255,255,0.8)] leading-relaxed border-l-[3px] border-l-[#FF3B30]">
                      "{prompt.content.substring(0, 100)}..."
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Help Text with Black Overlay */}
          <div className="w-full absolute bottom-0 left-0">
            <div className="bg-black px-4 pt-8 pb-[27px]">
              <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                <p>Create and manage custom prompts for your AI conversations.</p>
                <p>Click on any prompt to edit or use it in your chats.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        showCancel={modalConfig.showCancel}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
    </LayoutHeader>
  );
}

export default PromptsHub; 